import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Header } from '../../components/common/Header';
import { PersonalDataForm } from '../../components/profile/PersonalDataForm';
import { CompanyInfoForm } from '../../components/profile/CompanyInfoForm';
import { EmployeeList } from '../../components/profile/EmployeeList';
import { User, Company, CompanyEmployee, CompanyBankingDetails, EmployeeInviteRequest, UserRole } from '../../types/auth';
import { toast } from 'react-hot-toast';
import api from '../../services/api';

type TabType = 'personal' | 'company' | 'employees';

export const ProfilePage: React.FC = () => {
    const { user, updateUser } = useAuth();
    const [activeTab, setActiveTab] = useState<TabType>('personal');
    const [isLoading, setIsLoading] = useState(false);
    const [company, setCompany] = useState<Company | null>(null);
    const [bankingDetails, setBankingDetails] = useState<CompanyBankingDetails | null>(null);
    const [employees, setEmployees] = useState<CompanyEmployee[]>([]);

    useEffect(() => {
        console.log('ProfilePage useEffect - user:', user);
        console.log('ProfilePage useEffect - user.companyId:', user?.companyId);
        if (user?.companyId) {
            loadCompanyData();
        } else {
            console.log('No companyId found for user');
        }
    }, [user?.companyId]);

    const loadCompanyData = async () => {
        if (!user?.companyId) return;

        setIsLoading(true);
        try {
            console.log('Loading company data for companyId:', user.companyId);

            // Load company info
            const companyResponse = await api.getMyCompany();
            console.log('Company response:', companyResponse);
            if (companyResponse.data.success && companyResponse.data.data) {
                setCompany(companyResponse.data.data);
                console.log('Company data set:', companyResponse.data.data);
            }

            // Load banking details
            const bankingResponse = await api.getBankingDetails();
            console.log('Banking response:', bankingResponse);
            if (bankingResponse.data.success && bankingResponse.data.data) {
                setBankingDetails(bankingResponse.data.data);
                console.log('Banking details set:', bankingResponse.data.data);
            }

            // Load employees
            const employeesResponse = await api.getEmployees();
            console.log('Employees response:', employeesResponse);
            if (employeesResponse.data.success && employeesResponse.data.data) {
                setEmployees(employeesResponse.data.data);
                console.log('Employees data set:', employeesResponse.data.data);
            }
        } catch (error: any) {
            console.error('Error loading company data:', error);
            if (error.response?.status !== 404) {
                toast.error('Ошибка загрузки данных компании');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handlePersonalDataUpdate = async (data: any) => {
        try {
            const response = await api.updatePersonalData(data);
            if (response.data.success) {
                const updatedData = response.data.data;
                // Обновляем пользователя с новыми данными
                // fullName теперь приходит с бэкенда, но на всякий случай собираем его из частей если отсутствует
                const fullName = updatedData.fullName || 
                    [updatedData.lastName, updatedData.firstName, updatedData.middleName]
                        .filter(Boolean)
                        .join(' ');
                
                updateUser({
                    ...updatedData,
                    fullName,
                    companyId: user?.companyId // Сохраняем companyId
                });
                toast.success('Личные данные успешно обновлены');
            } else {
                toast.error(response.data.message || 'Ошибка обновления данных');
            }
        } catch (error: any) {
            console.error('Error updating personal data:', error);
            toast.error('Ошибка обновления личных данных');
        }
    };

    const handleCompanyInfoUpdate = async (data: any) => {
        try {
            const response = await api.updateCompanyInfo(data);
            if (response.data.success) {
                // Обновляем данные компании
                setCompany(response.data.data);
                
                // Перезагружаем банковские данные, чтобы отобразить обновленную информацию
                const bankingResponse = await api.getBankingDetails();
                if (bankingResponse.data.success && bankingResponse.data.data) {
                    setBankingDetails(bankingResponse.data.data);
                }
                
                toast.success('Контактные данные успешно обновлены');
            } else {
                toast.error(response.data.message || 'Ошибка обновления данных');
            }
        } catch (error: any) {
            console.error('Error updating company info:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Ошибка обновления контактных данных';
            toast.error(errorMessage);
        }
    };

    const handleEmployeeAdd = async (data: any) => {
        try {
            const response = await api.addEmployee(data);
            if (response.data.success) {
                setEmployees(prev => [...prev, response.data.data]);
                toast.success('Сотрудник успешно добавлен');
            } else {
                toast.error(response.data.message || 'Ошибка добавления сотрудника');
            }
        } catch (error: any) {
            console.error('Error adding employee:', error);
            toast.error('Ошибка добавления сотрудника');
        }
    };

    const handleEmployeeUpdate = async (employeeId: number, data: any) => {
        try {
            const response = await api.updateEmployee(employeeId, data);
            if (response.data.success) {
                setEmployees(prev =>
                    prev.map(emp => emp.id === employeeId ? response.data.data : emp)
                );
                toast.success('Данные сотрудника успешно обновлены');
            } else {
                toast.error(response.data.message || 'Ошибка обновления данных');
            }
        } catch (error: any) {
            console.error('Error updating employee:', error);
            toast.error('Ошибка обновления данных сотрудника');
        }
    };

    const handleEmployeeRemove = async (employeeId: number) => {
        try {
            const response = await api.removeEmployee(employeeId);
            if (response.data.success) {
                setEmployees(prev => prev.filter(emp => emp.id !== employeeId));
                toast.success('Сотрудник успешно удален');
            } else {
                toast.error(response.data.message || 'Ошибка удаления сотрудника');
            }
        } catch (error: any) {
            console.error('Error removing employee:', error);
            toast.error('Ошибка удаления сотрудника');
        }
    };

    const handleEmployeeInvite = async (data: EmployeeInviteRequest) => {
        if (!user?.companyId) {
            toast.error('Компания не найдена');
            return;
        }

        try {
            const response = await api.inviteEmployee(user.companyId, data);
            if (response.data.success) {
                toast.success('Приглашение отправлено сотруднику');
                // Обновляем список сотрудников
                loadCompanyData();
            } else {
                toast.error(response.data.message || 'Ошибка отправки приглашения');
            }
        } catch (error: any) {
            console.error('Error inviting employee:', error);
            toast.error('Ошибка отправки приглашения');
        }
    };

    const tabs = [
        {
            id: 'personal' as TabType,
            name: 'Личные данные',
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
            )
        },
        {
            id: 'company' as TabType,
            name: 'Информация о компании',
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-2M7 21H5m2 0h2m6-12h2m-2 4h2m-2 4h2M9 7h2m-2 4h2m-2 4h2" />
                </svg>
            )
        },
        {
            id: 'employees' as TabType,
            name: 'Сотрудники компании',
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                </svg>
            )
        }
    ];

    if (!user) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <Header
                showBackButton={true}
                backButtonText="На главную"
                backButtonPath="/client/dashboard"
                title="Профиль"
            />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                    {/* Tabs */}
                    <div className="border-b border-gray-200">
                        <nav className="flex space-x-8 px-6" aria-label="Tabs">
                            {tabs.map((tab) => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`${activeTab === tab.id
                                        ? 'border-blue-500 text-blue-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                        } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors`}
                                >
                                    {tab.icon}
                                    <span>{tab.name}</span>
                                </button>
                            ))}
                        </nav>
                    </div>

                    {/* Tab Content */}
                    <div className="p-6">
                        {isLoading ? (
                            <div className="flex items-center justify-center py-12">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                            </div>
                        ) : (
                            <>
                                {activeTab === 'personal' && (
                                    <PersonalDataForm
                                        user={user}
                                        onSubmit={handlePersonalDataUpdate}
                                    />
                                )}

                                {activeTab === 'company' && (
                                    <>
                                        {!user?.companyId ? (
                                            <div className="text-center py-12">
                                                <div className="text-gray-500 mb-4">
                                                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-2M7 21H5m2 0h2m6-12h2m-2 4h2m-2 4h2M9 7h2m-2 4h2m-2 4h2" />
                                                    </svg>
                                                </div>
                                                <h3 className="text-lg font-medium text-gray-900 mb-2">Компания не найдена</h3>
                                                <p className="text-gray-600 mb-4">
                                                    У вас нет привязанной компании. Обратитесь к администратору для решения этой проблемы.
                                                </p>
                                                <p className="text-sm text-gray-500">
                                                    User ID: {user?.id}, Company ID: {user?.companyId || 'не установлен'}
                                                </p>
                                            </div>
                                        ) : (
                                            <CompanyInfoForm
                                                company={company}
                                                bankingDetails={bankingDetails}
                                                onSubmit={handleCompanyInfoUpdate}
                                            />
                                        )}
                                    </>
                                )}

                                {activeTab === 'employees' && (
                                    <EmployeeList
                                        employees={employees}
                                        onAdd={handleEmployeeAdd}
                                        onUpdate={handleEmployeeUpdate}
                                        onRemove={handleEmployeeRemove}
                                        onInvite={user?.role === UserRole.SUPER_ADMIN ? handleEmployeeInvite : undefined}
                                        companyId={user?.companyId}
                                    />
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
